import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, ChevronDown, X, Check, Trash2, Upload, Camera, Video,
  Tag, Wallet, Boxes, Truck, Images, BadgeCheck, Palette,
  Save, AlertCircle, Wand2,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  upsertProduct,
  upsertProductMedia,
  deleteProductPhoto,
  listenToProductData,
} from "../../../redux/productSlice";
import { listenToUserCommercePartners } from "../../../redux/commercePartnersSlice";

/* ═══════════════════════════════════════════════════════════════
   GROCERY ONLY — nothing else. This screen never mounts the other
   verticals, so we only ever import the grocery branch of the
   category tree (categories/subcategories/products + the
   grocery-specific Quality/Variant vocabulary). Everything else
   that used to be hardcoded here (unit of sale, packaging, weight
   & length units, storage requirements, colour swatches) now comes
   straight from ProductsAttributes.tsx — nothing is duplicated.
═══════════════════════════════════════════════════════════════ */
import {
  groceryCategories,
  grocerySubcategories,
  groceryProducts,
  groceryVariants,
} from "../../../CATEGORIES/GroceryCategories";
import {
  type AttributeItem,
  productColors,
  productColorAttributes,
  GENERAL_COLOR_ATTRIBUTE_ID,
  Units,
  PackagingTypes,
  WeightUnits,
  LengthUnits,
  StorageRequirements,
} from "../../../CATEGORIES/ATTRIBUTES/ProductsAttributes";

/* ═══════════════════════════════════════════════════════════════
   TOKENS — identical palette to GroceriesHome, so the create flow
   and the storefront feel like the same shop, not two apps stitched
   together.
═══════════════════════════════════════════════════════════════ */
const T = {
  bg:         "#F7F6F2",
  bgMuted:    "#EEECE6",
  paper:      "#FFFFFF",
  bgDark:     "#14180F",
  ink:        "#14180F",
  inkMid:     "#2E3828",
  muted:      "#727868",
  mutedSoft:  "#A8AC9E",
  border:     "rgba(50,60,30,0.09)",
  green:      "#264D0E",
  greenMid:   "#3D7022",
  greenLight: "#6FA845",
  greenPale:  "#EEF5E8",
  saffron:    "#D97706",
  saffronPale:"#FBF0DD",
  red:        "#C0392B",
  cream:      "#FDF8EE",
  grad:       "linear-gradient(135deg,#264D0E 0%,#3D7022 45%,#D97706 100%)",
  gradH:      "linear-gradient(90deg,#264D0E 0%,#3D7022 55%,#D97706 100%)",
};
const GRAD_SIZE = "220% 100%";

/* ═══════════════════════════════════════════════════════════════
   ATTRIBUTE IDS — where selections get stored in the product's
   generic `attributes` bag. The *options* for each of these are
   sourced from ProductsAttributes, never hardcoded here.
═══════════════════════════════════════════════════════════════ */
const SALE_UNIT_ATTRIBUTE_ID = 90201; // references Units
const VARIANT_ATTRIBUTE_ID   = 90202; // references groceryVariants

const variantAttribute: AttributeItem = {
  id: VARIANT_ATTRIBUTE_ID,
  name: "Quality / Variant",
  values: groceryVariants as any,
  multiSelect: true,
} as any;

function resolveColorGroup(): AttributeItem | undefined {
  if (productColorAttributes?.values?.length) return productColorAttributes;
  return productColors.find((g) => g.id === GENERAL_COLOR_ATTRIBUTE_ID);
}

const MAX_IMAGES = 8;

/* ═══════════════════════════════════════════════════════════════
   GLOBAL CSS — Syne for the shopfront voice, Lora for a few serif
   accents, DM Sans for UI copy, and Space Mono for anything that
   reads like it came off a till: SKU, barcode, batch numbers.
   Also includes the 3D-sphere colour picker + its bottom-sheet /
   floating-panel chrome.
═══════════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=Lora:ital,wght@0,500;0,600;1,500&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=Space+Mono:wght@400;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  button,input,textarea,select{font-family:inherit;}
  ::-webkit-scrollbar{width:5px;height:5px;}
  ::-webkit-scrollbar-thumb{background:rgba(61,112,34,.22);border-radius:99px;}

  @keyframes gradSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes pulseDot{0%,100%{opacity:1}50%{opacity:.35}}
  @keyframes stampIn{from{opacity:0;transform:scale(1.4) rotate(-14deg)}to{opacity:1;transform:scale(1) rotate(-8deg)}}
  @keyframes agFloatIn{from{opacity:0;transform:translateY(-8px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}

  .ag-panel{background:#fff;border:1.5px solid rgba(50,60,30,.09);border-radius:20px;
            box-shadow:0 6px 28px rgba(38,77,14,.06);animation:fadeUp .3s ease both;}
  .ag-field{width:100%;border:1.5px solid rgba(50,60,30,.12);border-radius:10px;background:#F7F6F2;
            padding:10px 13px;font-size:13px;color:#14180F;outline:none;transition:border-color .15s,box-shadow .15s,background .15s;}
  .ag-field:focus{border-color:#3D7022;box-shadow:0 0 0 3px rgba(61,112,34,.1);background:#fff;}
  .ag-field::placeholder{color:#A8AC9E;}
  .ag-field:disabled{opacity:.5;cursor:not-allowed;}

  .ag-chip{padding:7px 14px;border-radius:999px;font-size:11.5px;font-weight:700;cursor:pointer;
           border:1.5px solid rgba(50,60,30,.12);background:#F7F6F2;color:#2E3828;transition:all .15s;
           display:inline-flex;align-items:center;gap:6px;}
  .ag-chip:hover{border-color:#6FA845;color:#264D0E;}
  .ag-chip.on{background:linear-gradient(90deg,#264D0E,#3D7022);color:#fff;border-color:transparent;
              box-shadow:0 3px 10px rgba(61,112,34,.32);}

  .ag-aisle-tab{display:flex;align-items:center;gap:10px;width:100%;padding:12px 14px;border-radius:12px;
                background:none;border:none;cursor:pointer;text-align:left;transition:background .15s,color .15s;color:#727868;}
  .ag-aisle-tab:hover{background:#F7F6F2;}
  .ag-aisle-tab.on{background:#EEF5E8;color:#264D0E;}
  .ag-aisle-tab .num{font-family:'Space Mono',monospace;font-size:10px;color:#A8AC9E;}
  .ag-aisle-tab.on .num{color:#3D7022;}

  .ag-media{width:78px;height:92px;border-radius:11px;position:relative;overflow:hidden;flex-shrink:0;
            border:1.5px solid rgba(50,60,30,.1);}
  .ag-media-add{width:78px;height:92px;border-radius:11px;border:1.5px dashed rgba(61,112,34,.4);
                background:rgba(61,112,34,.05);display:flex;flex-direction:column;align-items:center;
                justify-content:center;gap:4px;cursor:pointer;flex-shrink:0;transition:border-color .15s,background .15s;}
  .ag-media-add:hover{border-color:#3D7022;background:rgba(61,112,34,.09);}

  .ag-toggle{width:44px;height:25px;border-radius:13px;border:none;cursor:pointer;position:relative;flex-shrink:0;transition:background .2s;}
  .ag-toggle i{position:absolute;top:3px;width:19px;height:19px;border-radius:50%;background:#fff;transition:left .2s;box-shadow:0 1px 5px rgba(0,0,0,.25);}

  /* ── colour variant cards ── */
  .ag-variant-card{border:1.5px solid rgba(50,60,30,.12);border-radius:14px;background:#F7F6F2;padding:14px;transition:border-color .2s,box-shadow .2s;}
  .ag-variant-card:hover{border-color:rgba(61,112,34,.35);box-shadow:0 4px 16px rgba(61,112,34,.1);}

  /* ── 3D sphere colour picker ── */
  .ag-color-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(72px,1fr));gap:10px;}
  .ag-color-cell{display:flex;flex-direction:column;align-items:center;gap:6px;cursor:pointer;background:none;border:none;padding:0;}
  .ag-color-sphere{width:40px;height:40px;border-radius:50%;flex-shrink:0;transition:transform .18s,box-shadow .18s;
                    box-shadow:inset -6px -5px 12px rgba(0,0,0,.30),inset 5px 5px 10px rgba(255,255,255,.18),0 5px 18px rgba(0,0,0,.20);}
  .ag-color-cell:hover .ag-color-sphere{transform:scale(1.1) translateY(-2px);}
  .ag-color-sphere.sel{outline:2.5px solid #3D7022;outline-offset:3px;}
  .ag-color-sphere-multi{background:conic-gradient(#e53935 0deg 51deg,#FF9800 51deg 102deg,#FFEB3B 102deg 153deg,#228B22 153deg 204deg,#4169E1 204deg 255deg,#9C27B0 255deg 306deg,#e53935 306deg 360deg);}
  .ag-color-sphere-transparent{background:repeating-conic-gradient(#d0d0d0 0% 25%,#f8f8f8 0% 50%) 0 0/10px 10px;border:1.5px solid rgba(0,0,0,.10);box-shadow:none;}
  .ag-color-tick{position:absolute;bottom:-3px;right:-3px;width:16px;height:16px;border-radius:50%;background:#3D7022;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.3);}
  .ag-color-name{font-size:10px;font-weight:500;color:#727868;text-align:center;line-height:1.2;max-width:68px;word-break:break-word;}
  .ag-color-cell.sel .ag-color-name{color:#264D0E;font-weight:700;}

  .ag-sheet-backdrop{position:fixed;inset:0;background:rgba(20,24,15,.35);z-index:9999;display:flex;align-items:flex-end;}
  .ag-bottom-sheet{background:#fff;width:100%;max-height:74vh;border-top-left-radius:24px;border-top-right-radius:24px;
                   border-top:1.5px solid rgba(61,112,34,.25);overflow:hidden;display:flex;flex-direction:column;}
  .ag-attr-float{position:fixed;background:#fff;border-radius:16px;border:1.5px solid rgba(61,112,34,.28);
                 box-shadow:0 20px 64px rgba(20,24,15,.18),0 2px 20px rgba(61,112,34,.13);z-index:10000;
                 max-width:420px;max-height:420px;overflow-y:auto;padding:14px 16px 16px;animation:agFloatIn .18s ease both;}

  /* ── receipt (signature element) ── */
  .ag-receipt-wrap{position:relative;filter:drop-shadow(0 18px 36px rgba(20,24,15,.16));}
  .ag-receipt{background:#fffefb;background-image:repeating-linear-gradient(180deg,transparent 0 27px,rgba(50,60,30,.05) 27px 28px);
              padding:22px 20px 0;position:relative;transform:rotate(-.6deg);}
  .ag-receipt::before{content:'';position:absolute;top:-1px;left:0;right:0;height:10px;
              background:repeating-linear-gradient(115deg,#fffefb 0 9px,transparent 9px 10px),
                         repeating-linear-gradient(65deg,#fffefb 0 9px,transparent 9px 10px);
              background-position:0 0,0 0;clip-path:polygon(0 100%,0 0,5% 100%,10% 0,15% 100%,20% 0,25% 100%,30% 0,35% 100%,40% 0,45% 100%,50% 0,55% 100%,60% 0,65% 100%,70% 0,75% 100%,80% 0,85% 100%,90% 0,95% 100%,100% 0,100% 100%);}
  .ag-receipt-tear{height:12px;background:#fffefb;
              clip-path:polygon(0 0,100% 0,100% 40%,95% 100%,90% 40%,85% 100%,80% 40%,75% 100%,70% 40%,65% 100%,60% 40%,55% 100%,50% 40%,45% 100%,40% 40%,35% 100%,30% 40%,25% 100%,20% 40%,15% 100%,10% 40%,5% 100%,0 40%);}
  .ag-mono{font-family:'Space Mono',monospace;}
  .ag-rline{display:flex;justify-content:space-between;gap:10px;font-size:11px;color:#2E3828;padding:2px 0;}
  .ag-rdash{border-top:1.5px dashed rgba(50,60,30,.22);margin:10px 0;}
  .ag-barcode-bar{display:inline-block;background:#14180F;}
`;

/* ═══════════════════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════════════════ */
interface VariantShape {
  imageUrl?:   string;
  imageFile?:  File;
  previewUrl?: string;
  stock:       number;
}

interface FormState {
  listingId?: string;
  categoryId: number | null;
  subcategoryId: number | null;
  productId: number | null;

  // identity
  title: string;
  brand: string;
  sku: string;
  barcode: string;
  description: string;

  // pricing & stock
  price: number;
  cost: number;
  discount: number;
  stock: number;
  unitId: number | null;       // references Units (Gram/Kilogram/Piece/…)
  unitQuantity: number;        // e.g. 1 in "1 Kilogram", 500 in "500 Gram"
  variantIds: number[];        // Quality / Variant (groceryVariants)
  colorIds: number[];          // selected colours -> keys into `variants`
  variants: Record<number, VariantShape>; // per-colour photo + stock
  reorderThreshold: number;

  // product details
  weightVolumeValue: number;
  weightVolumeUnit: string;    // e.g. "g" | "kg" | "ml" | "l" (from WeightUnits)
  length: number;
  width: number;
  height: number;
  dimUnit: string;             // e.g. "cm" | "inch" (from LengthUnits)
  packagingType: string;       // name from PackagingTypes
  expirationDate: string;
  manufactureDate: string;
  batchLotNumber: string;

  // supplier & sourcing
  supplierName: string;
  supplierId: string;
  countryOfOrigin: string;
  storageRequirement: number | null; // id from StorageRequirements

  // media
  images: File[];
  photos: string[];
  video?: string | null;
  videoFile?: File | null;

  // publish
  available: boolean;
  isPublished: boolean;
}

const blank = (): FormState => ({
  categoryId: null, subcategoryId: null, productId: null,
  title: "", brand: "", sku: "", barcode: "", description: "",
  price: 0, cost: 0, discount: 0, stock: 0,
  unitId: null, unitQuantity: 1, variantIds: [], colorIds: [], variants: {},
  reorderThreshold: 0,
  weightVolumeValue: 0, weightVolumeUnit: WeightUnits[0]?.unit ?? "g",
  length: 0, width: 0, height: 0, dimUnit: LengthUnits[0]?.unit ?? "cm",
  packagingType: "", expirationDate: "", manufactureDate: "", batchLotNumber: "",
  supplierName: "", supplierId: "", countryOfOrigin: "", storageRequirement: null,
  images: [], photos: [], video: null, videoFile: null,
  available: true, isPublished: false,
});

const toggleMulti = (arr: number[] = [], val: number): number[] =>
  arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];

const fmt = (n: number) => `€${(Number(n) || 0).toFixed(2)}`;

function generateSku(subName: string | undefined, brand: string): string {
  const prefix = (subName || "GRC").replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase() || "GRC";
  const brandPart = (brand || "XX").replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || "XX";
  const digits = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${brandPart}-${digits}`;
}

/** Deterministic decorative barcode — visual texture only, not a real symbology. */
function barcodeWidths(seed: string): number[] {
  const s = seed || "0000000000";
  const widths: number[] = [];
  for (let i = 0; i < 46; i++) {
    const c = s.charCodeAt(i % s.length) || 48;
    widths.push(1 + (c % 4));
  }
  return widths;
}

/** Radial-gradient "3D sphere" look for a hex swatch. */
function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%,`,
    `  rgba(255,255,255,0.55) 0%,`,
    `  rgba(255,255,255,0.10) 28%,`,
    `  transparent 52%),`,
    `radial-gradient(circle at 68% 72%,`,
    `  rgba(0,0,0,0.30) 0%,`,
    `  transparent 55%),`,
    `radial-gradient(circle at 50% 50%,`,
    `  ${hex} 0%,`,
    `  ${hex} 100%)`,
  ].join(" ");
}

/* ═══════════════════════════════════════════════════════════════
   PRIMITIVES
═══════════════════════════════════════════════════════════════ */
const FL: React.FC<{ l: string; req?: boolean; hint?: string }> = ({ l, req, hint }) => (
  <div style={{ marginBottom: 6 }}>
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: T.muted, textTransform: "uppercase" as const }}>
      {l}{req && <span style={{ color: T.saffron, marginLeft: 3 }}>*</span>}
    </span>
    {hint && <span style={{ fontSize: 10, color: T.mutedSoft, marginLeft: 8 }}>{hint}</span>}
  </div>
);

const Field: React.FC<{
  value: string | number; onChange: (v: string) => void; placeholder?: string;
  type?: string; rows?: number; prefix?: string; suffix?: string; mono?: boolean;
}> = ({ value, onChange, placeholder, type = "text", rows, prefix, suffix, mono }) => (
  <div style={{ display: "flex", alignItems: "stretch", border: `1.5px solid ${T.border}`, borderRadius: 10, background: T.bg, overflow: "hidden" }}>
    {prefix && <div style={{ padding: "0 12px", display: "flex", alignItems: "center", background: T.greenPale, color: T.greenMid, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{prefix}</div>}
    {rows ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        className="ag-field" style={{ border: "none", borderRadius: 0, resize: "vertical", lineHeight: 1.6 }} />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type={type}
        className={mono ? "ag-field ag-mono" : "ag-field"} style={{ border: "none", borderRadius: 0 }} />
    )}
    {suffix && <div style={{ padding: "0 12px", display: "flex", alignItems: "center", color: T.muted, fontSize: 12, flexShrink: 0, whiteSpace: "nowrap" }}>{suffix}</div>}
  </div>
);

/** Lightweight inline dropdown — no portal needed for a single long-form page. */
const DropSelect: React.FC<{
  value: number | null; onChange: (v: number) => void;
  options: { id: number; name: string }[]; placeholder?: string; disabled?: boolean;
}> = ({ value, onChange, options, placeholder = "Select…", disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.id === value);
  useEffect(() => {
    const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className="ag-field"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: disabled ? "not-allowed" : "pointer", color: selected ? T.ink : T.mutedSoft, textAlign: "left" }}
      >
        {selected?.name ?? placeholder}
        <ChevronDown size={14} color={T.muted} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .18s", flexShrink: 0 }} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.14 }}
            style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 40, background: "#fff", border: `1.5px solid ${T.border}`, borderRadius: 12, boxShadow: "0 14px 40px rgba(20,24,15,.14)", maxHeight: 240, overflowY: "auto", padding: 6 }}
          >
            {options.length === 0 && <div style={{ padding: "10px 12px", fontSize: 12, color: T.mutedSoft }}>No options</div>}
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); setOpen(false); }}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 10px", border: "none", borderRadius: 8, background: value === o.id ? T.greenPale : "transparent", color: value === o.id ? T.greenMid : T.ink, fontSize: 12.5, fontWeight: value === o.id ? 700 : 500, cursor: "pointer", textAlign: "left" }}
              >
                {o.name}
                {value === o.id && <Check size={13} />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ChipGroup: React.FC<{
  options: { id: number; name: string }[]; multi?: boolean;
  current: number | number[] | undefined; onChange: (v: number | number[]) => void;
}> = ({ options, multi, current, onChange }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
    {options.map((o) => {
      const on = multi ? Array.isArray(current) && (current as number[]).includes(o.id) : current === o.id;
      return (
        <button key={o.id} type="button" className={`ag-chip${on ? " on" : ""}`}
          onClick={() => onChange(multi ? toggleMulti(Array.isArray(current) ? (current as number[]) : [], o.id) : o.id)}>
          {o.name}
        </button>
      );
    })}
  </div>
);

const ToggleSwitch: React.FC<{ on: boolean; onChange: (v: boolean) => void }> = ({ on, onChange }) => (
  <button type="button" className="ag-toggle" onClick={() => onChange(!on)} style={{ background: on ? T.greenMid : T.mutedSoft }}>
    <i style={{ left: on ? 22 : 3 }} />
  </button>
);

/* ═══════════════════════════════════════════════════════════════
   COLOUR PICKER — bottom sheet on mobile, floating panel on desktop.
   Swatches render as 3D spheres (radial-gradient), exactly like the
   general-catalogue colour picker, but themed to the grocery palette.
═══════════════════════════════════════════════════════════════ */
const ColorPickerField: React.FC<{
  selected: number[];
  colorGroup?: AttributeItem;
  onToggle: (id: number) => void;
}> = ({ selected, colorGroup, onToggle }) => {
  const [open, setOpen] = useState(false);
  const [isMob, setIsMob] = useState(window.innerWidth < 960);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef  = useRef<HTMLDivElement>(null);
  const [anchorRect, setAnchorRect] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const fn = () => setIsMob(window.innerWidth < 960);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!open || isMob) return;
    const update = () => {
      if (anchorRef.current) {
        const r = anchorRef.current.getBoundingClientRect();
        setAnchorRect({ top: r.bottom + 8, left: r.left, width: r.width });
      }
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => { window.removeEventListener("scroll", update, true); window.removeEventListener("resize", update); };
  }, [open, isMob]);

  useEffect(() => {
    if (!open || isMob) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, isMob]);

  useEffect(() => {
    if (!open) return;
    const fn = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [open]);

  const summary = selected.length
    ? selected.map((id) => colorGroup?.values?.find((v) => v.id === id)?.name).filter(Boolean).join(", ")
    : "Tap to select colours";

  const grid = colorGroup?.values ? (
    <div className="ag-color-grid">
      {colorGroup.values.map((color) => {
        const isSel         = selected.includes(color.id);
        const hex           = color.hexCodes?.[0];
        const isMulti       = color.name === "Multicolor";
        const isTransparent = color.name === "Transparent";
        return (
          <button
            key={color.id}
            type="button"
            className={`ag-color-cell${isSel ? " sel" : ""}`}
            onClick={() => onToggle(color.id)}
          >
            <div style={{ position: "relative", width: 40, height: 40, flexShrink: 0 }}>
              {isMulti ? (
                <div className={`ag-color-sphere ag-color-sphere-multi${isSel ? " sel" : ""}`} />
              ) : isTransparent ? (
                <div className={`ag-color-sphere ag-color-sphere-transparent${isSel ? " sel" : ""}`} />
              ) : hex ? (
                <div className={`ag-color-sphere${isSel ? " sel" : ""}`} style={{ background: sphere3D(hex) }} />
              ) : (
                <div className={`ag-color-sphere${isSel ? " sel" : ""}`} style={{ background: sphere3D(T.bgMuted) }} />
              )}
              {isSel && (
                <div className="ag-color-tick"><Check size={9} color="#fff" strokeWidth={3} /></div>
              )}
            </div>
            <span className="ag-color-name">{color.name}</span>
          </button>
        );
      })}
    </div>
  ) : (
    <p style={{ fontSize: 12, color: T.mutedSoft }}>No colours available</p>
  );

  const mobileSheet = (
    <motion.div className="ag-sheet-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}>
      <motion.div
        className="ag-bottom-sheet"
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ height: 3, background: T.gradH, backgroundSize: GRAD_SIZE, animation: "gradSpin 5s ease infinite", flexShrink: 0 }} />
        <div style={{ width: 36, height: 4, borderRadius: 2, background: T.mutedSoft, margin: "12px auto 8px", flexShrink: 0 }} />
        <p style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 15, color: T.ink, padding: "4px 20px 12px", flexShrink: 0 }}>Colour</p>
        <div style={{ padding: "0 20px 16px", overflowY: "auto" }}>{grid}</div>
        <div style={{ padding: "10px 20px 20px", flexShrink: 0 }}>
          <button
            onClick={() => setOpen(false)}
            style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: T.gradH, backgroundSize: GRAD_SIZE, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Syne',sans-serif" }}
          >
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  const desktopFloat = anchorRect ? (
    <motion.div
      ref={panelRef}
      className="ag-attr-float"
      style={{ top: anchorRect.top, left: anchorRect.left, width: Math.max(320, anchorRect.width) }}
      initial={{ opacity: 0, y: -8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.97 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <p style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 13, color: T.ink }}>Colour</p>
        <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex", padding: 2 }}>
          <X size={14} />
        </button>
      </div>
      {grid}
      <button
        onClick={() => setOpen(false)}
        style={{ marginTop: 12, width: "100%", padding: "9px", borderRadius: 10, border: "none", background: T.gradH, backgroundSize: GRAD_SIZE, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "'Syne',sans-serif" }}
      >
        Done
      </button>
    </motion.div>
  ) : null;

  return (
    <div>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="ag-field"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", textAlign: "left" }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6, color: selected.length ? T.ink : T.mutedSoft, fontSize: 13, flexWrap: "wrap" }}>
          {selected.slice(0, 6).map((id) => {
            const c = colorGroup?.values?.find((v) => v.id === id);
            const hex = c?.hexCodes?.[0];
            return (
              <span key={id} style={{ width: 16, height: 16, borderRadius: "50%", background: hex ? sphere3D(hex) : sphere3D(T.bgMuted), border: "1px solid rgba(0,0,0,.12)", flexShrink: 0 }} />
            );
          })}
          <span style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</span>
        </span>
        <ChevronDown size={14} color={T.muted} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .18s", flexShrink: 0 }} />
      </button>
      {createPortal(
        <AnimatePresence>
          {open && (isMob ? mobileSheet : desktopFloat)}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   COLOUR VARIANT CARD — one per selected colour: its own photo
   (defaults to nothing until picked) + its own stock count.
═══════════════════════════════════════════════════════════════ */
const GroceryVariantCard: React.FC<{
  colorId: number;
  colorName: string;
  hex?: string;
  variant: VariantShape | undefined;
  fallbackStock: number;
  onUpdate: (id: number, patch: Partial<VariantShape>) => void;
  onRemove: (id: number) => void;
}> = ({ colorId, colorName, hex, variant, fallbackStock, onUpdate, onRemove }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const preview = variant?.previewUrl ?? variant?.imageUrl;
  return (
    <motion.div className="ag-variant-card" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {hex && (
            <div style={{ width: 14, height: 14, borderRadius: "50%", background: sphere3D(hex), border: "1.5px solid rgba(0,0,0,0.12)" }} />
          )}
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", color: T.ink }}>{colorName.toUpperCase()}</span>
        </div>
        <button onClick={() => onRemove(colorId)} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, padding: 4 }}>
          <X size={14} />
        </button>
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <div
          onClick={() => fileRef.current?.click()}
          style={{ width: 78, height: 92, borderRadius: 10, background: T.bgMuted, border: "1.5px dashed rgba(61,112,34,.4)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", flexShrink: 0, gap: 4 }}
        >
          {preview ? (
            <img src={preview} alt={colorName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <>
              <Camera size={16} color={T.greenMid} />
              <span style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em", fontWeight: 700 }}>PHOTO</span>
            </>
          )}
          <input ref={fileRef} type="file" hidden accept="image/*" onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            onUpdate(colorId, { imageFile: f, previewUrl: URL.createObjectURL(f) });
          }} />
        </div>
        <div style={{ flex: 1 }}>
          <FL l="Stock" />
          <Field value={variant?.stock ?? fallbackStock ?? 0} onChange={(v) => onUpdate(colorId, { stock: +v })} type="number" placeholder="0" />
        </div>
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   AISLE DEFINITIONS — "Colour Variants" only appears once at least
   one colour has been picked, and re-tags A1/A2/… automatically.
═══════════════════════════════════════════════════════════════ */
type AisleId = "identity" | "pricing" | "variants" | "details" | "supplier" | "media" | "publish";
const ALL_AISLES: { id: AisleId; label: string; icon: any }[] = [
  { id: "identity", label: "Identity",           icon: Tag },
  { id: "pricing",  label: "Pricing & Stock",     icon: Wallet },
  { id: "variants", label: "Colour Variants",     icon: Palette },
  { id: "details",  label: "Product Details",     icon: Boxes },
  { id: "supplier", label: "Supplier & Sourcing", icon: Truck },
  { id: "media",    label: "Media",               icon: Images },
  { id: "publish",  label: "Publish",             icon: BadgeCheck },
];

/* ═══════════════════════════════════════════════════════════════
   RECEIPT — the signature element. A running till-tape preview of
   the listing being built, with a decorative barcode drawn from the
   SKU/barcode text and a "ready to publish" stamp once the core
   fields are filled in.
═══════════════════════════════════════════════════════════════ */
const ReadinessStamp: React.FC<{ pct: number }> = ({ pct }) => {
  const ready = pct >= 100;
  return (
    <div style={{
      position: "absolute", top: 14, right: 10, width: 64, height: 64, borderRadius: "50%",
      border: `2.5px solid ${ready ? T.greenMid : T.saffron}`, display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column", transform: "rotate(-8deg)", animation: "stampIn .35s ease both", opacity: 0.9,
      color: ready ? T.greenMid : T.saffron, background: ready ? T.greenPale : T.saffronPale,
    }}>
      <span style={{ fontFamily: "'Space Mono',monospace", fontWeight: 700, fontSize: 15, lineHeight: 1 }}>{pct}%</span>
      <span style={{ fontSize: 6.5, fontWeight: 800, letterSpacing: "0.08em" }}>{ready ? "READY" : "FILLING"}</span>
    </div>
  );
};

const Barcode: React.FC<{ seed: string }> = ({ seed }) => {
  const widths = useMemo(() => barcodeWidths(seed || "GROCERY0000"), [seed]);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 1, height: 34 }}>
        {widths.map((w, i) => (
          <span key={i} className="ag-barcode-bar" style={{ width: w, height: i % 7 === 0 ? 34 : 26 }} />
        ))}
      </div>
      <div className="ag-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: T.inkMid, marginTop: 4, textAlign: "center" }}>
        {(seed || "PENDING-SKU").toUpperCase()}
      </div>
    </div>
  );
};

const ReceiptSidebar: React.FC<{ form: FormState; unitLabel: string; totalStock: number; catName?: string }> = ({ form, unitLabel, totalStock, catName }) => {
  const now = useMemo(() => new Date(), []);
  const requiredFields: (keyof FormState)[] = ["title", "sku", "price", "categoryId", "unitId"];
  const filled = requiredFields.filter((k) => {
    const v = form[k] as any;
    return v !== null && v !== undefined && v !== "" && v !== 0;
  }).length;
  const withPhoto = form.photos.length + form.images.length > 0 ? 1 : 0;
  const pct = Math.round(((filled + withPhoto) / (requiredFields.length + 1)) * 100);

  const subtotal = form.price || 0;
  const discountAmt = subtotal * ((form.discount || 0) / 100);
  const net = Math.max(subtotal - discountAmt, 0);

  const storageInfo = StorageRequirements.find((s) => s.id === form.storageRequirement);

  return (
    <div className="ag-receipt-wrap">
      <div className="ag-receipt" style={{ position: "relative" }}>
        <ReadinessStamp pct={pct} />
        <div style={{ textAlign: "center", marginBottom: 10 }}>
          <p style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 15, color: T.ink, letterSpacing: -0.2 }}>GROCER'S TICKET</p>
          <p className="ag-mono" style={{ fontSize: 9.5, color: T.mutedSoft, marginTop: 2 }}>
            {now.toLocaleDateString("en-GB")} · {now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>

        <div className="ag-rdash" />

        <div className="ag-rline"><span style={{ color: T.mutedSoft }}>ITEM</span><span style={{ fontWeight: 700, textAlign: "right", maxWidth: 160 }}>{form.title || "Untitled product"}</span></div>
        {form.brand && <div className="ag-rline"><span style={{ color: T.mutedSoft }}>BRAND</span><span>{form.brand}</span></div>}
        {catName && <div className="ag-rline"><span style={{ color: T.mutedSoft }}>AISLE</span><span>{catName}</span></div>}

        <div className="ag-rdash" />

        <div className="ag-rline"><span style={{ color: T.mutedSoft }}>UNIT PRICE</span><span className="ag-mono">{fmt(form.price)}{unitLabel ? ` / ${unitLabel}` : ""}</span></div>
        {form.discount > 0 && (
          <div className="ag-rline"><span style={{ color: T.saffron }}>PROMO −{form.discount}%</span><span className="ag-mono" style={{ color: T.saffron }}>−{fmt(discountAmt)}</span></div>
        )}
        <div className="ag-rline"><span style={{ color: T.mutedSoft }}>STOCK ON HAND</span><span className="ag-mono">{totalStock} units</span></div>

        <div className="ag-rdash" />

        <div className="ag-rline" style={{ fontSize: 13 }}>
          <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, color: T.ink }}>TOTAL</span>
          <span className="ag-mono" style={{ fontWeight: 700, color: T.greenMid, fontSize: 15 }}>{fmt(net)}</span>
        </div>

        <div className="ag-rdash" />

        {(form.expirationDate || storageInfo) && (
          <div style={{ display: "flex", gap: 10, fontSize: 10, color: T.mutedSoft, marginBottom: 8, flexWrap: "wrap" }}>
            {form.expirationDate && <span>USE BY {form.expirationDate}</span>}
            {storageInfo && <span>· {storageInfo.name}</span>}
          </div>
        )}

        <div style={{ padding: "6px 0 18px" }}>
          <Barcode seed={form.barcode || form.sku} />
        </div>
      </div>
      <div className="ag-receipt-tear" />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   TOAST
═══════════════════════════════════════════════════════════════ */
const Toast: React.FC<{ visible: boolean; msg: string; type: "ok" | "err" | "warn" }> = ({ visible, msg, type }) => (
  <AnimatePresence>
    {visible && (
      <motion.div
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 18 }}
        style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
          background: type === "ok" ? T.greenMid : type === "err" ? T.red : T.saffron,
          color: "#fff", fontSize: 12.5, fontWeight: 700, padding: "12px 24px", borderRadius: 999,
          zIndex: 9999, boxShadow: "0 10px 30px rgba(20,24,15,.2)", display: "flex", alignItems: "center", gap: 8,
        }}>
        {type === "ok" ? <Check size={14} /> : <AlertCircle size={14} />}
        {msg}
      </motion.div>
    )}
  </AnimatePresence>
);

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const AddGrocery: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { listingId: pid } = useParams<{ listingId?: string }>();
  const userId = useSelector((s: RootState) => s.auth.user?.userId);
  const partners = useSelector((s: RootState) => s.commercePartners.partners);
  const currentProduct = useSelector((s: RootState) => s.products.currentProduct);


  const groceryBrandId = (partners as any[]).find(
    (p) => Number(p.categoryId) === 1 && Number(p.subcategoryId) === 101
  )?.brandId;

  const [form, setForm] = useState<FormState>(blank());
  const [aisle, setAisle] = useState<AisleId>("identity");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [isMob, setIsMob] = useState(window.innerWidth < 960);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; type: "ok" | "err" | "warn" }>({ open: false, msg: "", type: "ok" });

  const toast = (msg: string, type: "ok" | "err" | "warn" = "ok") => {
    setSnack({ open: true, msg, type });
    setTimeout(() => setSnack((s) => ({ ...s, open: false })), 3000);
  };

  useEffect(() => {
    const fn = () => setIsMob(window.innerWidth < 960);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  useEffect(() => { if (userId) dispatch(listenToUserCommercePartners(userId) as any); }, [userId, dispatch]);
  useEffect(() => { if (pid) dispatch(listenToProductData(pid)); }, [dispatch, pid]);

  useEffect(() => {
    if (!pid || !currentProduct) return;
    const attrs: any = (currentProduct as any).attributes ?? {};
    const details: any = (currentProduct as any).groceryDetails ?? {};
    setForm({
      listingId: currentProduct.listingId,
      categoryId: currentProduct.categoryId ?? null,
      subcategoryId: (currentProduct as any).subcategoryId ?? null,
      productId: currentProduct.productId ?? null,
      title: currentProduct.name ?? "",
      brand: details.brand ?? "",
      sku: details.sku ?? "",
      barcode: details.barcode ?? "",
      description: (currentProduct as any).description ?? "",
      price: currentProduct.rate ?? 0,
      cost: details.cost ?? 0,
      discount: details.discount ?? 0,
      stock: currentProduct.stock ?? 0,
      unitId: attrs[SALE_UNIT_ATTRIBUTE_ID] ?? null,
      unitQuantity: details.unitQuantity ?? 1,
      variantIds: attrs[VARIANT_ATTRIBUTE_ID] ?? [],
      colorIds: attrs[GENERAL_COLOR_ATTRIBUTE_ID] ?? [],
      variants: currentProduct.variants
        ? Object.fromEntries(Object.entries(currentProduct.variants).map(([k, v]: any) => [
            Number(k), { imageUrl: v.imageUrl, imageFile: undefined, previewUrl: undefined, stock: v.stock ?? 0 }
          ]))
        : {},
      reorderThreshold: details.reorderThreshold ?? 0,
      weightVolumeValue: details.weightVolumeValue ?? 0,
      weightVolumeUnit: details.weightVolumeUnit ?? (WeightUnits[0]?.unit ?? "g"),
      length: details.length ?? 0,
      width: details.width ?? 0,
      height: details.height ?? 0,
      dimUnit: details.dimUnit ?? (LengthUnits[0]?.unit ?? "cm"),
      packagingType: details.packagingType ?? "",
      expirationDate: details.expirationDate ?? "",
      manufactureDate: details.manufactureDate ?? "",
      batchLotNumber: details.batchLotNumber ?? "",
      supplierName: details.supplierName ?? "",
      supplierId: details.supplierId ?? "",
      countryOfOrigin: details.countryOfOrigin ?? "",
      storageRequirement: details.storageRequirement ?? null,
      images: [], photos: currentProduct.photos ?? [],
      video: currentProduct.videoUrl ?? null, videoFile: null,
      available: currentProduct.available ?? true,
      isPublished: currentProduct.isPublished ?? false,
    });
    setDirty(false);
  }, [currentProduct, pid]);

  const up = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((p) => ({ ...p, [key]: value }));
    setDirty(true);
  };

  const isEdit = !!pid;
  const subs = grocerySubcategories.filter((s: any) => s.parentId === form.categoryId);
  const prods = groceryProducts.filter((p: any) => p.parentId === form.subcategoryId);
  const catName = groceryCategories.find((c: any) => c.id === form.categoryId)?.name;
  const subName = grocerySubcategories.find((s: any) => s.id === form.subcategoryId)?.name;
  const colorGroup = resolveColorGroup();

  /* ── colour → variant derivation, same pattern as the general Add Product screen ── */
  const genColors    = form.colorIds;
  const hasVariants  = genColors.length > 0;
  const totalStock   = hasVariants
    ? Object.values(form.variants).reduce((s, v) => s + (v?.stock || 0), 0)
    : form.stock;
  const unitName  = form.unitId != null ? (Units.find((u) => u.id === form.unitId)?.name ?? "") : "";
  const unitLabel = unitName ? `${form.unitQuantity && form.unitQuantity !== 1 ? `${form.unitQuantity} ` : ""}${unitName}` : "";

  const AISLES = useMemo(() => {
    const list = ALL_AISLES.filter((a) => a.id !== "variants" || hasVariants);
    return list.map((a, i) => ({ ...a, tag: `A${i + 1}` }));
  }, [hasVariants]);

  useEffect(() => {
    if (!hasVariants && aisle === "variants") setAisle("pricing");
  }, [hasVariants, aisle]);

  const toggleColor = (id: number) => {
    setForm((p) => {
      const nextIds = toggleMulti(p.colorIds, id);
      const variants = { ...p.variants };
      if (!nextIds.includes(id)) delete variants[id];
      return { ...p, colorIds: nextIds, variants };
    });
    setDirty(true);
  };
  const updateVariant = (colorId: number, patch: Partial<VariantShape>) => {
    setForm((p) => {
      const ex = p.variants?.[colorId];
      return { ...p, variants: { ...p.variants, [colorId]: { ...(ex ?? { stock: 0 }), ...patch } } };
    });
    setDirty(true);
  };
  const removeVariant = (colorId: number) => {
    setForm((p) => {
      const nv = { ...p.variants }; delete nv[colorId];
      return { ...p, variants: nv, colorIds: p.colorIds.filter((id) => id !== colorId) };
    });
    setDirty(true);
  };

  const handleImages = (files: FileList | null) => {
    if (!files) return;
    const remaining = MAX_IMAGES - form.photos.length - form.images.length;
    if (remaining <= 0) { toast("Maximum 8 photos allowed", "warn"); return; }
    up("images", [...form.images, ...Array.from(files).slice(0, remaining)]);
  };
  const handleDelPhoto = (url: string) => {
    if (!form.listingId) return;
    dispatch(deleteProductPhoto({ listingId: form.listingId, photoUrl: url }));
    up("photos", form.photos.filter((p) => p !== url));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast("Item name is required", "warn"); setAisle("identity"); return; }
    if (!form.categoryId) { toast("Pick an aisle (category) first", "warn"); setAisle("identity"); return; }
    setSaving(true);
    const attributes: Record<number, number | number[]> = {};
    if (form.unitId != null) attributes[SALE_UNIT_ATTRIBUTE_ID] = form.unitId;
    if (form.variantIds.length) attributes[VARIANT_ATTRIBUTE_ID] = form.variantIds;
    if (form.colorIds.length) attributes[GENERAL_COLOR_ATTRIBUTE_ID] = form.colorIds;

    try {
      const saved = await dispatch(upsertProduct({
        listingId: form.listingId,
        data: {
          name: form.title, description: form.description,
          categoryId: form.categoryId, subcategoryId: form.subcategoryId, productId: form.productId,
          rate: form.price, available: form.available, isPublished: form.isPublished, isDraft: !form.isPublished,
          operatorId: groceryBrandId ?? "",
          attributes,
          ...(hasVariants ? {} : { stock: form.stock }),
          groceryDetails: {
            brand: form.brand, sku: form.sku, barcode: form.barcode,
            cost: form.cost, discount: form.discount, reorderThreshold: form.reorderThreshold,
            unitQuantity: form.unitQuantity,
            weightVolumeValue: form.weightVolumeValue, weightVolumeUnit: form.weightVolumeUnit,
            length: form.length, width: form.width, height: form.height, dimUnit: form.dimUnit,
            packagingType: form.packagingType, expirationDate: form.expirationDate,
            manufactureDate: form.manufactureDate, batchLotNumber: form.batchLotNumber,
            supplierName: form.supplierName, supplierId: form.supplierId,
            countryOfOrigin: form.countryOfOrigin, storageRequirement: form.storageRequirement,
          },
        },
      })).unwrap();

      if (hasVariants) {
        const updatedVariants = { ...form.variants };
        for (const [cid, vrt] of Object.entries(form.variants)) {
          if (!vrt.imageFile) continue;
          const res = await dispatch(upsertProductMedia({ listingId: saved.listingId, files: [vrt.imageFile], type: "photo" })).unwrap();
          const url = res.downloadURLs?.[0];
          if (url) {
            updatedVariants[Number(cid)] = { ...vrt, imageUrl: url, imageFile: undefined, previewUrl: url };
            updateVariant(Number(cid), { imageUrl: url, imageFile: undefined, previewUrl: url });
          }
        }
        await dispatch(upsertProduct({
          listingId: saved.listingId,
          data: {
            variants: Object.fromEntries(
              Object.entries(updatedVariants)
                .filter(([, v]) => !!v.imageUrl)
                .map(([k, v]) => [k, { colorId: +k, imageUrl: v.imageUrl!, stock: v.stock ?? 0 }])
            ),
          },
        })).unwrap();
      }

      if (form.images.length > 0) await dispatch(upsertProductMedia({ listingId: saved.listingId, files: form.images, type: "photo" })).unwrap();
      if (form.videoFile) await dispatch(upsertProductMedia({ listingId: saved.listingId, files: [form.videoFile], type: "video" })).unwrap();

      toast("Ticket printed — item saved ✓");
      setForm((p) => ({ ...p, listingId: saved.listingId, images: [], videoFile: null }));
      setDirty(false);
    } catch (err) {
      console.error(err);
      toast("Save failed", "err");
    } finally {
      setSaving(false);
    }
  };

  /* ═══════════════════════════════════════════
     AISLE CONTENT
  ═══════════════════════════════════════════ */
  const renderIdentity = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
        <div>
          <FL l="Category" req />
          <DropSelect value={form.categoryId} onChange={(v) => { up("categoryId", v); up("subcategoryId", null); up("productId", null); }}
            options={groceryCategories.map((c: any) => ({ id: c.id, name: c.name }))} placeholder="Pick an aisle…" />
        </div>
        <div>
          <FL l="Subcategory" />
          <DropSelect value={form.subcategoryId} onChange={(v) => { up("subcategoryId", v); up("productId", null); }}
            options={subs.map((s: any) => ({ id: s.id, name: s.name }))} placeholder="Pick a shelf…" disabled={!form.categoryId} />
        </div>
        <div>
          <FL l="Product Type" />
          <DropSelect value={form.productId} onChange={(v) => up("productId", v)}
            options={prods.map((p: any) => ({ id: p.id, name: p.name }))} placeholder="Pick a type…" disabled={!form.subcategoryId} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: 12 }}>
        <div>
          <FL l="Item Name" req />
          <Field value={form.title} onChange={(v) => up("title", v)} placeholder="e.g. Whole Milk 1L" />
        </div>
        <div>
          <FL l="Brand" />
          <Field value={form.brand} onChange={(v) => up("brand", v)} placeholder="e.g. Valio" />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: 12 }}>
        <div>
          <FL l="Item ID / SKU" req hint="unique per listing" />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Field value={form.sku} onChange={(v) => up("sku", v)} placeholder="GRC-XX-0000" mono />
            </div>
            <button type="button" onClick={() => up("sku", generateSku(subName, form.brand))}
              title="Generate SKU"
              style={{ flexShrink: 0, width: 40, borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.greenPale, color: T.greenMid, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <Wand2 size={15} />
            </button>
          </div>
        </div>
        <div>
          <FL l="Barcode" hint="UPC / EAN" />
          <Field value={form.barcode} onChange={(v) => up("barcode", v)} placeholder="e.g. 4740098000123" mono />
        </div>
      </div>

      <div>
        <FL l="Description" />
        <Field value={form.description} onChange={(v) => up("description", v)} placeholder="What makes this item worth a spot on the shelf…" rows={4} />
      </div>
    </div>
  );

  const renderPricing = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 12 }}>
        <div>
          <FL l="Price" req />
          <Field value={form.price} onChange={(v) => up("price", +v)} type="number" prefix="€" placeholder="0.00" suffix={unitLabel ? `/ ${unitLabel}` : undefined} />
        </div>
        <div>
          <FL l="Cost (wholesale)" />
          <Field value={form.cost} onChange={(v) => up("cost", +v)} type="number" prefix="€" placeholder="0.00" />
        </div>
        <div>
          <FL l="Discount / Promo" />
          <Field value={form.discount} onChange={(v) => up("discount", +v)} type="number" suffix="%" placeholder="0" />
        </div>
        <div>
          <FL l="Reorder Threshold" />
          <Field value={form.reorderThreshold} onChange={(v) => up("reorderThreshold", +v)} type="number" suffix="units" placeholder="0" />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 2fr", gap: 12 }}>
        {!hasVariants && (
          <div>
            <FL l="Quantity in Stock" req />
            <Field value={form.stock} onChange={(v) => up("stock", +v)} type="number" suffix="units" placeholder="0" />
          </div>
        )}
        <div>
          <FL l="Sold By" req hint="e.g. 1 Kilogram, 500 Gram, 1 Piece" />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ width: 90, flexShrink: 0 }}>
              <Field value={form.unitQuantity} onChange={(v) => up("unitQuantity", +v)} type="number" placeholder="1" />
            </div>
            <div style={{ flex: 1 }}>
              <DropSelect value={form.unitId} onChange={(v) => up("unitId", v)}
                options={Units.map((u) => ({ id: u.id, name: u.name }))} placeholder="Kilogram, Litre, Piece…" />
            </div>
          </div>
        </div>
      </div>

      <div>
        <FL l="Quality / Variant" hint="optional, multiple allowed" />
        <ChipGroup multi options={(groceryVariants as any[]).map((v) => ({ id: v.id, name: v.name }))} current={form.variantIds} onChange={(v) => up("variantIds", v as number[])} />
      </div>

      <div>
        <FL l="Colour" hint="optional — pick colours to create photo variants" />
        <ColorPickerField selected={form.colorIds} colorGroup={colorGroup} onToggle={toggleColor} />
      </div>
    </div>
  );

  const renderVariants = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p style={{ fontSize: 12, color: T.muted }}>Each colour gets its own photo and stock count on the shelf.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {genColors.map((cid) => {
          const cv = colorGroup?.values?.find((v) => v.id === cid);
          if (!cv) return null;
          return (
            <GroceryVariantCard
              key={cid}
              colorId={cid}
              colorName={cv.name}
              hex={cv.hexCodes?.[0]}
              variant={form.variants?.[cid]}
              fallbackStock={form.stock}
              onUpdate={updateVariant}
              onRemove={removeVariant}
            />
          );
        })}
      </div>
    </div>
  );

  const renderDetails = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr 1fr" : "2fr 1fr", gap: 12, alignItems: "end" }}>
        <div>
          <FL l="Weight / Volume" />
          <Field value={form.weightVolumeValue} onChange={(v) => up("weightVolumeValue", +v)} type="number" placeholder="0" />
        </div>
        <div>
          <DropSelect
            value={WeightUnits.find((u) => u.unit === form.weightVolumeUnit)?.id ?? WeightUnits[0]?.id ?? null}
            onChange={(id) => { const wu = WeightUnits.find((u) => u.id === id); if (wu?.unit) up("weightVolumeUnit", wu.unit); }}
            options={WeightUnits.map((u) => ({ id: u.id, name: u.unit ?? u.name }))}
          />
        </div>
      </div>

      <div>
        <FL l="Dimensions" hint={`L × W × H, in ${form.dimUnit}`} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 10 }}>
          <Field value={form.length} onChange={(v) => up("length", +v)} type="number" placeholder="Length" />
          <Field value={form.width} onChange={(v) => up("width", +v)} type="number" placeholder="Width" />
          <Field value={form.height} onChange={(v) => up("height", +v)} type="number" placeholder="Height" />
          <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
            {LengthUnits.map((u) => (
              <button key={u.id} type="button" onClick={() => up("dimUnit", u.unit ?? u.name)}
                style={{ padding: "0 12px", border: "none", background: form.dimUnit === (u.unit ?? u.name) ? T.greenMid : T.bg, color: form.dimUnit === (u.unit ?? u.name) ? "#fff" : T.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {u.unit ?? u.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <FL l="Packaging Type" />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
          {PackagingTypes.map((p) => (
            <button key={p.id} type="button" className={`ag-chip${form.packagingType === p.name ? " on" : ""}`} onClick={() => up("packagingType", p.name)}>
              {p.icon}<span>{p.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: 12 }}>
        <div>
          <FL l="Manufacture Date" />
          <Field value={form.manufactureDate} onChange={(v) => up("manufactureDate", v)} type="date" />
        </div>
        <div>
          <FL l="Expiration Date / Shelf Life" />
          <Field value={form.expirationDate} onChange={(v) => up("expirationDate", v)} type="date" />
        </div>
      </div>

      <div>
        <FL l="Batch / Lot Number" />
        <Field value={form.batchLotNumber} onChange={(v) => up("batchLotNumber", v)} placeholder="e.g. L-240311-A" mono />
      </div>
    </div>
  );

  const renderSupplier = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: 12 }}>
        <div>
          <FL l="Supplier Name" />
          <Field value={form.supplierName} onChange={(v) => up("supplierName", v)} placeholder="e.g. Baltic Farms Co-op" />
        </div>
        <div>
          <FL l="Supplier ID" />
          <Field value={form.supplierId} onChange={(v) => up("supplierId", v)} placeholder="e.g. SUP-00214" mono />
        </div>
      </div>

      <div>
        <FL l="Country of Origin" />
        <Field value={form.countryOfOrigin} onChange={(v) => up("countryOfOrigin", v)} placeholder="e.g. Estonia" />
      </div>

      <div>
        <FL l="Storage Requirements" />
        <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "repeat(3,1fr)", gap: 10 }}>
          {StorageRequirements.map((s) => {
            const sel = form.storageRequirement === s.id;
            return (
              <button key={s.id} type="button" onClick={() => up("storageRequirement", sel ? null : s.id)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${sel ? T.greenMid : T.border}`, background: sel ? T.greenPale : T.bg, cursor: "pointer", textAlign: "left" }}>
                <span style={{ color: sel ? T.greenMid : T.muted, display: "flex" }}>{s.icon}</span>
                <div>
                  <p style={{ fontSize: 12.5, fontWeight: 700, color: sel ? T.greenMid : T.ink }}>{s.name}</p>
                  <p style={{ fontSize: 10, color: T.mutedSoft }}>{s.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderMedia = () => (
    <div>
      <p style={{ fontSize: 11.5, color: T.muted, marginBottom: 12 }}>{form.photos.length + form.images.length}/{MAX_IMAGES} photos</p>
      <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6 }}>
        {form.photos.map((url) => (
          <div key={url} className="ag-media">
            <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <button onClick={() => handleDelPhoto(url)} style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(192,57,43,.88)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Trash2 size={10} color="#fff" />
            </button>
          </div>
        ))}
        {form.images.map((f, i) => (
          <div key={i} className="ag-media">
            <img src={URL.createObjectURL(f)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <button onClick={() => up("images", form.images.filter((_, j) => j !== i))} style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(192,57,43,.88)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Trash2 size={10} color="#fff" />
            </button>
          </div>
        ))}
        {form.photos.length + form.images.length < MAX_IMAGES && (
          <label className="ag-media-add">
            <Upload size={16} color={T.greenMid} />
            <span style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.08em", color: T.greenMid }}>PHOTO</span>
            <input type="file" hidden multiple accept="image/*" onChange={(e) => handleImages(e.target.files)} />
          </label>
        )}
        {!form.video && !form.videoFile && (
          <label className="ag-media-add">
            <Video size={16} color={T.muted} />
            <span style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.08em", color: T.muted }}>VIDEO</span>
            <input type="file" hidden accept="video/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) up("videoFile", f); }} />
          </label>
        )}
        {(form.video || form.videoFile) && (
          <div className="ag-media">
            <video src={form.videoFile ? URL.createObjectURL(form.videoFile) : form.video!} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <button onClick={() => { up("video", null); up("videoFile", null); }} style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(192,57,43,.88)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Trash2 size={10} color="#fff" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderPublish = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 0", borderBottom: `1px solid ${T.border}` }}>
        <div>
          <p style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Available</p>
          <p style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Shoppers can add it to their basket</p>
        </div>
        <ToggleSwitch on={form.available} onChange={(v) => up("available", v)} />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 0" }}>
        <div>
          <p style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Published</p>
          <p style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Visible in Groceries search & aisles</p>
        </div>
        <ToggleSwitch on={form.isPublished} onChange={(v) => up("isPublished", v)} />
      </div>

      <button
        onClick={dirty && !saving ? handleSave : undefined}
        disabled={!dirty || saving}
        style={{
          marginTop: 18, display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
          border: "none", borderRadius: 14, padding: "15px 0", width: "100%", fontWeight: 800, fontSize: 14,
          cursor: dirty && !saving ? "pointer" : "not-allowed", letterSpacing: "0.02em",
          background: dirty ? T.gradH : T.bgMuted, backgroundSize: GRAD_SIZE, color: dirty ? "#fff" : T.mutedSoft,
          boxShadow: dirty ? "0 8px 26px rgba(38,77,14,.28)" : "none",
        }}
      >
        {saving ? "Printing ticket…" : <><Save size={15} /> {isEdit ? "Update Item" : "Save Item"}</>}
      </button>
    </div>
  );

  const AISLE_CONTENT: Record<AisleId, () => React.ReactNode> = {
    identity: renderIdentity, pricing: renderPricing, variants: renderVariants, details: renderDetails,
    supplier: renderSupplier, media: renderMedia, publish: renderPublish,
  };
  const activeAisle = AISLES.find((a) => a.id === aisle) ?? AISLES[0];

  /* ═══════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'DM Sans',sans-serif", paddingBottom: 60 }}>
      <style>{CSS}</style>

      {/* header */}
      <div style={{ background: "#fff", borderBottom: `1px solid ${T.border}`, position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ height: 3, background: T.gradH, backgroundSize: GRAD_SIZE, animation: "gradSpin 5s ease infinite" }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 20px", maxWidth: 1240, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => navigate(-1)} style={{ width: 36, height: 36, border: `1.5px solid ${T.border}`, borderRadius: 10, background: T.bgMuted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.muted }}>
              <ChevronLeft size={19} />
            </button>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", color: T.mutedSoft }}>{isEdit ? "EDITING GROCERY ITEM" : "NEW GROCERY ITEM"}</div>
              <h1 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: isMob ? 17 : 21, color: T.ink, letterSpacing: -0.3 }}>{form.title || "Add Grocery Item"}</h1>
            </div>
          </div>
          {!isMob && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.mutedSoft }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: dirty ? T.saffron : T.greenLight, animation: dirty ? "pulseDot 1.4s infinite" : "none" }} />
                {dirty ? "Unsaved changes" : "All changes saved"}
              </span>
              <button
                onClick={dirty && !saving ? handleSave : undefined} disabled={!dirty || saving}
                style={{ display: "flex", alignItems: "center", gap: 8, border: "none", borderRadius: 12, padding: "10px 22px", fontWeight: 800, fontSize: 13, cursor: dirty && !saving ? "pointer" : "not-allowed", background: dirty ? T.gradH : T.bgMuted, backgroundSize: GRAD_SIZE, color: dirty ? "#fff" : T.mutedSoft, boxShadow: dirty ? "0 6px 20px rgba(38,77,14,.28)" : "none" }}
              >
                <Save size={14} /> {saving ? "Saving…" : isEdit ? "Update" : "Save"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* mobile aisle chips */}
      {isMob && (
        <div style={{ display: "flex", gap: 8, overflowX: "auto", padding: "12px 14px", background: "#fff", borderBottom: `1px solid ${T.border}` }}>
          {AISLES.map((a) => {
            const Icon = a.icon;
            const on = aisle === a.id;
            return (
              <button key={a.id} onClick={() => setAisle(a.id)}
                style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 6, padding: "8px 13px", borderRadius: 999, border: `1.5px solid ${on ? T.greenMid : T.border}`, background: on ? T.greenPale : T.bg, color: on ? T.greenMid : T.muted, fontSize: 11.5, fontWeight: 700 }}>
                <Icon size={13} /> {a.label}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ maxWidth: 1240, margin: "0 auto", padding: isMob ? "16px 14px" : "26px 28px" }}>
        <div style={{ display: isMob ? "block" : "grid", gridTemplateColumns: "220px 1.6fr 340px", gap: 22, alignItems: "start" }}>

          {/* aisle nav — desktop only */}
          {!isMob && (
            <div style={{ position: "sticky", top: 90 }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", color: T.mutedSoft, marginBottom: 10, paddingLeft: 4 }}>AISLES</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {AISLES.map((a) => {
                  const Icon = a.icon;
                  return (
                    <button key={a.id} className={`ag-aisle-tab${aisle === a.id ? " on" : ""}`} onClick={() => setAisle(a.id)}>
                      <Icon size={16} />
                      <span style={{ fontSize: 12.5, fontWeight: 700, flex: 1 }}>{a.label}</span>
                      <span className="num">{a.tag}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* active aisle panel */}
          <div className="ag-panel" style={{ padding: isMob ? "18px 16px" : "26px 28px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: T.greenPale, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <activeAisle.icon size={17} color={T.greenMid} />
              </div>
              <div>
                <p style={{ fontFamily: "'Lora',serif", fontStyle: "italic", fontSize: 11, color: T.saffron }}>Aisle {activeAisle.tag.slice(1)}</p>
                <p style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 17, color: T.ink }}>{activeAisle.label}</p>
              </div>
            </div>
            {AISLE_CONTENT[aisle]()}
          </div>

          {/* receipt sidebar — desktop only, sticky */}
          {!isMob && (
            <div style={{ position: "sticky", top: 90 }}>
              <ReceiptSidebar form={form} unitLabel={unitLabel} totalStock={totalStock} catName={subName || catName} />
            </div>
          )}
        </div>

        {isMob && (
          <div style={{ marginTop: 22 }}>
            <ReceiptSidebar form={form} unitLabel={unitLabel} totalStock={totalStock} catName={subName || catName} />
          </div>
        )}
      </div>

      {/* mobile sticky save */}
      {isMob && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 16px 20px", background: "#fff", borderTop: `1px solid ${T.border}`, zIndex: 90 }}>
          <button
            onClick={dirty && !saving ? handleSave : undefined} disabled={!dirty || saving}
            style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 9, border: "none", borderRadius: 14, padding: "14px 0", fontWeight: 800, fontSize: 14, cursor: dirty && !saving ? "pointer" : "not-allowed", background: dirty ? T.gradH : T.bgMuted, backgroundSize: GRAD_SIZE, color: dirty ? "#fff" : T.mutedSoft }}
          >
            <Save size={15} /> {saving ? "Saving…" : isEdit ? "Update Item" : "Save Item"}
          </button>
        </div>
      )}

      <Toast visible={snack.open} msg={snack.msg} type={snack.type} />
    </div>
  );
};

export default AddGrocery;